import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { getIssues, Issue, IssueList } from '../../api/issues';
import { previewRequeue, requeueIssues, RequeuePreview } from '../data/cockpit';
import { useCockpit } from '../data/CockpitContext';
import { focusLabel } from '../data/focus';
import { ago, until, when } from '../data/format';
import { apiError, remember, usePoll, useNow } from '../data/usePoll';
import { Card, Empty, EnvChip, ErrorNotice, Loading, Meter, Owner, OwnerChip, Spinner } from '../components/ui';
import { v2 } from '../paths';
import { attemptsLabel, issueCacheKey, issueLabel, objectName, ownerOfGroup, ownerOfIssue, verdictLook } from './issueModel';
import { AfterDeploy, reclassified } from './AfterDeploy';
import { getReevaluations, ReevaluationSummary } from '../../api/reevaluation';
import { useInEnvFrame } from './EnvLayout';

type OwnerFilter = Owner | 'all';

const OWNER_FILTERS: OwnerFilter[] = ['us', 'cu', 'au', 'all'];

/**
 * The owner tile to show: the one in the URL (?wie=), else the owner of the group a link pointed at (?code=), so a
 * click on a customer's problem from Live or the dashboard lands on that problem, else ours.
 */
export const ownerToShow = (wie: string | null, code: string | undefined, groups: { code: string }[], ownerOf: (g: any) => Owner): OwnerFilter => {
    if (wie && (OWNER_FILTERS as string[]).includes(wie)) return wie as OwnerFilter;
    const linked = code ? groups.find((g) => g.code === code) : undefined;
    return linked ? ownerOf(linked) : 'us';
};

/** What an issue says about requeueing when /Cockpit/requeue/preview is not there yet. */
const localVerdict = (i: Issue): RequeuePreview => ({
    customer: i.customer,
    environment: i.environment,
    key: i.key,
    verdict: !i.requeueHelps ? 'na' : i.nextAutoRequeueAt || i.status === 'ResolvesItself' ? 'auto' : 'unknown',
    nextAt: i.nextAutoRequeueAt,
    reason: null,
});

export const IssuesPage = () => {
    const { customer, environment } = useParams();
    const axios = useAxiosSwagger();
    const navigate = useNavigate();
    const location = useLocation();
    const { shown: environments, focus } = useCockpit();
    // Without an environment in the URL the list follows the header's focus.
    const listCustomer = customer ?? focus?.customer;
    const listEnvironment = customer ? environment : focus?.environment;
    const now = useNow(1000);
    const [params, setParams] = useSearchParams();
    const tab = params.get('tab') === 'deploy' ? 'deploy' : 'backlog';
    const code = params.get('code') ?? undefined;
    const [preview, setPreview] = useState<Record<string, RequeuePreview>>({});
    const [offering, setOffering] = useState<{ at: Date; keys: string[]; note?: string } | null>(null);
    const [offerError, setOfferError] = useState<string>();

    const list = usePoll<IssueList>(
        axios ? (s) => getIssues(axios, { customer: listCustomer, environment: listEnvironment, status: 'open' }, s) : null,
        20000,
        [axios, listCustomer, listEnvironment],
        `issues/${listCustomer ?? '*'}/${listEnvironment ?? '*'}`
    );

    const scoped = !!customer && !!environment;
    const inFrame = useInEnvFrame();
    const runs = usePoll<ReevaluationSummary[]>(axios ? (s) => getReevaluations(axios, s) : null, 30000, [axios], 'reevaluations');
    const done = useMemo(() => reclassified(runs.data, environments), [runs.data, environments]);
    // Only a watch that saw another build, and only until the backlog was re-classified: after that the banner and the
    // "oude indeling" tags are wrong. A watch started by hand ahead of a deploy is no new build.
    const deployed = environments.filter(
        (e) =>
            e.newBuildWatched === true &&
            !done.has(`${e.customer}/${e.environment}`.toLowerCase()) &&
            (!scoped || (e.customer === customer && e.environment === environment))
    );
    // A deploy being watched means new rules; an issue nobody judged again since may still have the old ones.
    const deployedKeys = new Set(deployed.map((d) => `${d.customer}/${d.environment}`.toLowerCase()));

    const openGroups = useMemo(() => (list.data?.groups ?? []).filter((g) => g.open > 0), [list.data]);
    const owner = ownerToShow(params.get('wie'), code, openGroups, ownerOfGroup);
    const groups = useMemo(() => {
        return openGroups
            .filter((g) => owner === 'all' || ownerOfGroup(g) === owner)
            .sort((a, b) => b.waitingOnUs + b.needsRequeue - (a.waitingOnUs + a.needsRequeue) || b.open - a.open);
    }, [openGroups, owner]);

    const selected = groups.find((g) => g.code === code) ?? groups[0];
    const items = useMemo(() => (list.data?.items ?? []).filter((i) => selected && i.code === selected.code && i.status !== 'Resolved' && i.status !== 'Closed'), [list.data, selected]);

    // The requeue column: ask the server what would happen, without offering anything.
    useEffect(() => {
        if (!axios || items.length === 0) return;
        const controller = new AbortController();
        const refs = items.slice(0, 50).map((i) => ({ customer: i.customer, environment: i.environment, key: i.key }));

        previewRequeue(axios, refs, controller.signal)
            .then((rows) => setPreview((p) => ({ ...p, ...Object.fromEntries(rows.map((r) => [r.key, r])) })))
            .catch((e) => {
                if (controller.signal.aborted) return;
                setPreview((p) => ({ ...p, ...Object.fromEntries(items.map((i) => [i.key, localVerdict(i)])) }));
            });

        return () => controller.abort();
    }, [axios, items]);

    const may = items.filter((i) => preview[i.key]?.verdict === 'may');
    const counts = {
        auto: items.filter((i) => preview[i.key]?.verdict === 'auto').length,
        may: may.length,
        no: items.filter((i) => preview[i.key]?.verdict === 'no').length,
    };

    const offer = async () => {
        if (!axios || may.length === 0) return;
        setOfferError(undefined);
        try {
            const results = await requeueIssues(axios, may.slice(0, 25).map((i) => ({ customer: i.customer, environment: i.environment, key: i.key })));
            const refused = results.filter((r) => r.state === 'Refused' || r.state === 'Failed');
            setOffering({ at: new Date(), keys: results.map((r) => r.item.key), note: refused.length ? `${refused.length} tegengehouden` : undefined });
            list.reload();
        } catch (e: any) {
            setOfferError(apiError(e));
        }
    };

    const c = list.data?.counts;
    const ownerTiles: { k: OwnerFilter; label: string; sub: string; n?: number; tone: string; cls: string }[] = [
        { k: 'us', label: 'Wij', sub: 'actie nodig', n: c ? c.waitingOnUs + c.needsRequeue : undefined, tone: 'bad', cls: 'us' },
        { k: 'cu', label: 'Klant', sub: 'actie nodig', n: c?.waitingOnCustomer, tone: 'warn', cls: 'cu' },
        { k: 'au', label: 'Vanzelf', sub: 'geen actie', n: c?.resolvesItself, tone: '', cls: '' },
        { k: 'all', label: 'Alles', sub: 'open', n: c?.open, tone: '', cls: '' },
    ];

    const setTab = (t: 'backlog' | 'deploy') => {
        const next = new URLSearchParams(params);
        if (t === 'deploy') next.set('tab', 'deploy');
        else next.delete('tab');
        setParams(next);
    };

    // The tile in the URL, so it survives opening an issue, Esc and the back button.
    const setOwner = (o: OwnerFilter) => {
        const next = new URLSearchParams(params);
        next.set('wie', o);
        next.delete('code');
        setParams(next);
    };

    const pickGroup = (g: string) => {
        const next = new URLSearchParams(params);
        next.set('wie', owner);
        next.set('code', g);
        setParams(next);
    };

    return (
        <>
            {inFrame ? (
                <div className="toolbar">
                    <span className="h2">Issues</span>
                    <span className="mut" style={{ fontSize: 12 }}>{list.loadedAt ? `bijgewerkt ${ago(list.loadedAt, now)} geleden` : 'laden…'}</span>
                </div>
            ) : (
                <div className="pagehead">
                    <div>
                        <div className="eyebrow">{scoped ? `${customer} · ${environment}` : focus ? focusLabel(focus) : 'Alle omgevingen'}</div>
                        <h1 className="h1">Issues</h1>
                    </div>
                    <span className="mut" style={{ fontSize: 12 }}>{list.loadedAt ? `bijgewerkt ${ago(list.loadedAt, now)} geleden` : 'laden…'}</span>
                </div>
            )}

            <div className="tabs" role="tablist">
                <button className={tab === 'backlog' ? 'tab on' : 'tab'} onClick={() => setTab('backlog')}>
                    Backlog <span className="cnt">{c?.open ?? '…'}</span>
                </button>
                <button className={tab === 'deploy' ? 'tab on' : 'tab'} onClick={() => setTab('deploy')}>
                    Na deploy {deployed.length > 0 && <span className="cnt amb">{deployed.length} omgevingen</span>}
                </button>
            </div>

            {tab === 'deploy' && <AfterDeploy customer={customer} environment={environment} />}

            {tab === 'backlog' && (
                <>
                    {deployed.length > 0 && (
                        <section className="card" style={{ background: '#15120A', borderColor: '#5C4512' }}>
                            <div className="cb hrow" style={{ gap: 14 }}>
                                <span className="lamp warn" style={{ margin: 0 }} />
                                <span style={{ flex: '1 1 300px' }}>
                                    <b>Nieuwe build op {deployed.map((d) => `${d.customer} ${d.environment}`).join(', ')}.</b>{' '}
                                    <span className="mut">Issues van vóór de deploy kunnen nog in de oude indeling staan.</span>
                                </span>
                                <button className="btn" onClick={() => setTab('deploy')}>Herbeoordelen</button>
                            </div>
                        </section>
                    )}

                    <div className="tiles">
                        {ownerTiles.map((o) => (
                            <button key={o.k} className={`card tile ${owner === o.k ? o.cls : ''}`} style={owner === o.k && !o.cls ? { borderColor: '#34404F' } : undefined} onClick={() => setOwner(o.k)}>
                                <span className="l">
                                    {o.k !== 'all' ? <OwnerChip owner={o.k as Owner} /> : <span className="own au">Alles</span>}
                                    {o.sub}
                                </span>
                                <span className={`v ${o.tone}`}>{o.n ?? '…'}</span>
                            </button>
                        ))}
                    </div>

                    <ErrorNotice error={list.error} />
                    {list.pending && <Loading>Issues ophalen…</Loading>}

                    <div className="g2" style={{ gridTemplateColumns: 'minmax(280px, 0.8fr) minmax(0, 1.7fr)' }}>
                        <Card title="Probleemgroepen" right={<span className="mut" style={{ fontSize: 12 }}>{groups.length}</span>}>
                            {list.data && groups.length === 0 && <Empty>Niets in deze selectie.</Empty>}
                            {groups.map((g) => {
                                const o = ownerOfGroup(g);
                                const max = Math.max(...groups.map((x) => x.open), 1);
                                return (
                                    <button key={g.code} className={`li ${selected?.code === g.code ? 'on' : ''}`} style={{ gridTemplateColumns: '12px minmax(0,1fr) 44px' }} onClick={() => pickGroup(g.code)}>
                                        <span className={`sev ${o}`} />
                                        <span>
                                            <span className="t" style={{ fontWeight: 500 }}>{g.title}</span>
                                            <span className="m">
                                                <Meter pct={(g.open / max) * 100} tone={o === 'us' ? 'r' : o === 'cu' ? 'w' : 'i'} width={100} />
                                                {!scoped && <span>{g.environments.length === 1 ? g.environments[0] : `${g.environments.length} omgevingen`}</span>}
                                                {g.new > 0 && <span className="warn">{g.new} nieuw</span>}
                                            </span>
                                        </span>
                                        <span className="mono" style={{ textAlign: 'right' }}>{g.open}</span>
                                    </button>
                                );
                            })}
                        </Card>

                        <Card
                            title={
                                selected ? (
                                    <div>
                                        <div className="h2">{selected.title}</div>
                                        <div className="mut" style={{ fontSize: 12, marginTop: 2 }}>{selected.code}{selected.unrecognised ? ' · nog geen regel' : ''}</div>
                                    </div>
                                ) : (
                                    'Issues'
                                )
                            }
                            right={selected ? <OwnerChip owner={ownerOfGroup(selected)} long /> : undefined}
                        >
                            {selected && (
                                <div className="g3" style={{ gap: 1, background: '#1A212B', borderBottom: '1px solid #1A212B' }}>
                                    <div style={{ background: '#10151B', padding: '10px 16px' }}><div className="eyebrow">Gaat vanzelf</div><div className="mono" style={{ fontSize: 20 }}>{counts.auto}</div></div>
                                    <div style={{ background: '#10151B', padding: '10px 16px' }}><div className="eyebrow">Kun jij aanbieden</div><div className="mono ok" style={{ fontSize: 20 }}>{counts.may}</div></div>
                                    <div style={{ background: '#10151B', padding: '10px 16px' }}><div className="eyebrow">Tegengehouden</div><div className="mono bad" style={{ fontSize: 20 }}>{counts.no}</div></div>
                                </div>
                            )}
                            <div className="tw">
                                <table className="tbl">
                                    <thead>
                                        <tr>
                                            <th>Object</th>
                                            {!scoped && <th>Waar</th>}
                                            <th>Wie</th>
                                            <th>Opnieuw aanbieden</th>
                                            <th className="num" title="Sinds de eerste fout">Open</th>
                                            <th className="num" title="Sinds de laatste fout">Laatst</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {items.slice(0, 100).map((i) => {
                                            const p = preview[i.key];
                                            const look = offering?.keys.includes(i.key) ? { text: 'Aangeboden', cls: 'chip info' } : verdictLook(p);
                                            return (
                                                <tr key={i.key} className="hov" onClick={() => {
                                                        remember(issueCacheKey(i.customer, i.environment, i.key), i);
                                                        // The tile and group go along, so the list behind the panel and after Esc stays where it was.
                                                        const here = new URLSearchParams(location.search);
                                                        here.set('wie', owner);
                                                        if (selected) here.set('code', selected.code);
                                                        navigate(`${(scoped ? v2.issue : v2.globalIssue)(i.customer, i.environment, i.key)}?${here.toString()}`);
                                                    }}>
                                                    <td>
                                                        <span className="mono">{issueLabel(i)}</span>
                                                        <div className="mut" style={{ fontSize: 12, marginTop: 2 }}>{[objectName(i.objectType), attemptsLabel(i)].filter(Boolean).join(' · ')}</div>
                                                        {scoped && deployedKeys.has(`${i.customer}/${i.environment}`.toLowerCase()) && !i.reviewedAt && <div><span className="chip warn" style={{ padding: '0 6px', marginTop: 4 }}>oude indeling</span></div>}
                                                    </td>
                                                    {!scoped && (
                                                        <td>
                                                            <EnvChip kind={i.environment.toLowerCase().startsWith('prod') ? 'prod' : 'test'} environment={i.environment} /> {i.customer}
                                                            {deployedKeys.has(`${i.customer}/${i.environment}`.toLowerCase()) && !i.reviewedAt && <div><span className="chip warn" style={{ padding: '0 6px', marginTop: 4 }}>oude indeling</span></div>}
                                                        </td>
                                                    )}
                                                    <td><OwnerChip owner={ownerOfIssue(i)} /></td>
                                                    <td>
                                                        <span className={look.cls}>{look.text}</span>
                                                        {p?.verdict === 'auto' && p.nextAt && <span className="mut mono" style={{ fontSize: 11, marginLeft: 6 }}>over {until(p.nextAt, now)}</span>}
                                                        {'reason' in look && look.reason && (
                                                            <div className="mut" style={{ fontSize: 12, marginTop: 4, maxWidth: 440, whiteSpace: 'normal' }}>{look.reason}</div>
                                                        )}
                                                    </td>
                                                    <td className="num" title={when(i.firstFailedAt)}>{ago(i.firstFailedAt, now)}</td>
                                                    <td className="num" title={when(i.lastFailedAt)}>{ago(i.lastFailedAt, now)}</td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {items.length > 100 && <Empty>De eerste 100 van {items.length}; filter op omgeving voor de rest.</Empty>}
                                {selected && items.length === 0 && list.data?.itemsOmitted && <Empty>Kies een omgeving om de objecten te zien.</Empty>}
                            </div>
                            {selected && (counts.may > 0 || offering) && (
                                <div className="ch" style={{ borderTop: '1px solid #1A212B', borderBottom: 0, justifyContent: 'flex-end' }}>
                                    {offerError && <span className="bad" style={{ fontSize: 13 }}>{offerError}</span>}
                                    {offering && (
                                        <span className="info hrow" style={{ fontSize: 13, gap: 6 }}>
                                            <Spinner />
                                            {offering.keys.length} aangeboden, {ago(offering.at, now)} geleden
                                            {offering.note ? ` · ${offering.note}` : ''}
                                        </span>
                                    )}
                                    <span className="mut" style={{ fontSize: 13 }}>Alleen wat door de controles komt</span>
                                    <button className="btn pri" onClick={offer} disabled={counts.may === 0}>
                                        Aanbieden · {Math.min(25, counts.may)}
                                    </button>
                                </div>
                            )}
                        </Card>
                    </div>

                    {scoped && (
                        <div className="hrow">
                            <Link className="btn ghost" to={v2.issues()}>Alle omgevingen</Link>
                        </div>
                    )}
                </>
            )}
        </>
    );
};
